// ============================================================================
// remote/remoteSession.ts — «Удалённый доступ» (Remote): протокол шины и
// утилиты захвата. Сигналинг — по ОБЩЕЙ шине (как звонки):
//   remote.session.started.{sessionId}   — инициатор (viewer) приглашает
//   remote.session.declined.{sessionId}  — отказ/невозможность (host)
//   remote.session.ended.{sessionId}     — завершение любой стороной
// Роль: инициатор = viewer (просит показать экран), принимающий = host
// (делится экраном; подтверждение ВРУЧНУЮ — клик «Показать экран»).
// Комната LiveKit: remote_{sessionId}.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { logEvent } from '@/lib/eventLog';

export type RemoteRole = 'viewer' | 'host';

export const remoteTopic = {
  started: (sessionId: string) => `remote.session.started.${sessionId}`,
  declined: (sessionId: string) => `remote.session.declined.${sessionId}`,
  ended: (sessionId: string) => `remote.session.ended.${sessionId}`,
} as const;

/** Имя комнаты LiveKit для сеанса удалённого доступа. */
export const remoteRoomName = (sessionId: string): string => `remote_${sessionId}`;

/** Новый идентификатор сеанса (URL-safe; формат u-<hex> / chat-<id>). */
export function newRemoteSessionId(prefix = 'u'): string {
  const uuid = (globalThis.crypto as Crypto | undefined)?.randomUUID?.();
  const raw = uuid || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}-${raw.replace(/-/g, '').slice(0, 24)}`;
}

/** Публикация сигнала сеанса; ошибки — в журнал регистрации (не молча). */
export function publishRemoteSignal(topic: string, payload: Record<string, unknown>): void {
  getSDK().eventBus.publish({ topic, payload: JSON.stringify({ ...payload, v: __APP_VERSION__ }) })
    .catch((e: unknown) => {
      void logEvent({
        level: 'error',
        category: 'ui',
        message: 'удалённый доступ: не удалось отправить сигнал',
        entityType: 'remote',
        entityCode: 'session',
        objectId: topic,
        details: String((e as Error)?.message || e),
      });
    });
}

// Пресеты качества показа экрана (R1): захват + лимит публикации.
// Применяются при НАЧАЛЕ показа; fps — live через applyConstraints.
export interface RemoteQualityPreset {
  code: 'auto' | 'high' | 'medium' | 'low';
  label: string;
  width: number;
  height: number;
  fps: number;
  maxBitrate: number;
}

export const REMOTE_QUALITY_PRESETS: RemoteQualityPreset[] = [
  { code: 'auto', label: 'Авто (1080p30)', width: 1920, height: 1080, fps: 30, maxBitrate: 8_000_000 },
  { code: 'high', label: 'Высокое (1080p60)', width: 1920, height: 1080, fps: 60, maxBitrate: 12_000_000 },
  { code: 'medium', label: 'Среднее (720p30)', width: 1280, height: 720, fps: 30, maxBitrate: 4_000_000 },
  { code: 'low', label: 'Низкое (720p15)', width: 1280, height: 720, fps: 15, maxBitrate: 1_500_000 },
];

export function remotePreset(code: string): RemoteQualityPreset {
  return REMOTE_QUALITY_PRESETS.find((p) => p.code === code) || REMOTE_QUALITY_PRESETS[0];
}

// Захват ЭКРАНА (host). Вызывать ТОЛЬКО из обработчика клика — иначе движок
// заблокирует без user gesture. contentHint='text' — чёткость интерфейса (R1).
export async function captureScreenStream(preset?: RemoteQualityPreset): Promise<MediaStream> {
  const md = navigator.mediaDevices as MediaDevices | undefined;
  if (!md || typeof md.getDisplayMedia !== 'function') {
    throw new Error('Захват экрана не поддерживается этим окружением');
  }
  const q = preset || REMOTE_QUALITY_PRESETS[0];
  const stream = await md.getDisplayMedia({
    video: {
      frameRate: { ideal: q.fps },
      width: { ideal: q.width },
      height: { ideal: q.height },
    },
    audio: false,
  } as DisplayMediaStreamOptions);
  const track = stream.getVideoTracks()[0];
  if (!track) {
    throw new Error('Не удалось получить видеодорожку экрана');
  }
  try {
    track.contentHint = 'text';
  } catch {
    /* движок без contentHint — не критично */
  }
  let settings: MediaTrackSettings = {};
  try {
    settings = track.getSettings();
  } catch {
    settings = {};
  }
  void logEvent({
    level: 'info',
    category: 'ui',
    message: 'удалённый доступ: экран захвачен',
    entityType: 'remote',
    entityCode: 'session',
    details: JSON.stringify(settings),
  });
  return stream;
}

// --- Управление (Ф2): контракт data-сообщений — plans/remote-protocol-contract.md ---

/** Событие ввода (viewer → host). Координаты — нормализованные 0..1.
 *  Относительный режим (Pointer Lock): mouse_move_rel несёт dx/dy в пикселях,
 *  а mouse_button/mouse_wheel с rel=true применяются в ТЕКУЩЕЙ позиции курсора. */
export interface RemoteInputEvent {
  type: 'mouse_move' | 'mouse_move_rel' | 'mouse_button' | 'mouse_wheel' | 'key_text' | 'key_scancode';
  x?: number;
  y?: number;
  dx?: number;
  dy?: number;
  rel?: boolean;
  button?: 'left' | 'right' | 'middle';
  down?: boolean;
  delta?: number;
  text?: string;
  code?: number;
}

/** Кодирование data-сообщения LiveKit (JSON → bytes). */
export function encodeRemoteData(obj: unknown): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(JSON.stringify(obj));
}

/** Декодирование data-сообщения LiveKit (bytes → JSON); битое — null. */
export function decodeRemoteData(payload: Uint8Array): Record<string, unknown> | null {
  try {
    return JSON.parse(new TextDecoder().decode(payload)) as Record<string, unknown>;
  } catch {
    return null;
  }
}
